<script setup lang="ts">
import { computed } from 'vue';
import type { TrendMonthDto } from '@/api/reports';

interface Props {
  months: TrendMonthDto[];
}

const props = defineProps<Props>();

// Compute the max total across all months so bars are proportional.
const maxTotal = computed(() =>
  Math.max(
    1,
    ...props.months.map(m => m.critical + m.high + m.medium + m.low),
  ),
);

const chartHeight = 80; // px

function barHeight(count: number): number {
  return (count / maxTotal.value) * chartHeight;
}

function formatMonthLabel(yyyy_mm: string): string {
  const [year, mm] = yyyy_mm.split('-');
  const d = new Date(Number(year), Number(mm) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short' });
}
</script>

<template>
  <div
    class="flex items-end gap-2 overflow-x-auto pb-2"
    role="img"
    aria-label="Monthly issues trend by severity"
    data-testid="report-trend-chart"
  >
    <div
      v-for="m in months"
      :key="m.month"
      class="flex flex-col items-center gap-1 flex-1 min-w-[32px]"
    >
      <!-- Stacked bar: critical (bottom-most = most urgent) -->
      <div
        class="w-full flex flex-col-reverse rounded-t overflow-hidden"
        :style="{ height: `${chartHeight}px` }"
        :title="`${m.month}: ${m.critical + m.high + m.medium + m.low} total`"
      >
        <!-- Low — navy -->
        <div
          class="w-full bg-navy-400 transition-all duration-300"
          :style="{ height: `${barHeight(m.low)}px` }"
        />
        <!-- Medium — amber -->
        <div
          class="w-full bg-amber-400 transition-all duration-300"
          :style="{ height: `${barHeight(m.medium)}px` }"
        />
        <!-- High — orange -->
        <div
          class="w-full bg-orange-400 transition-all duration-300"
          :style="{ height: `${barHeight(m.high)}px` }"
        />
        <!-- Critical — red (top of stack) -->
        <div
          class="w-full bg-red-500 transition-all duration-300"
          :style="{ height: `${barHeight(m.critical)}px` }"
        />
      </div>
      <!-- Month label -->
      <span class="text-xs text-muted font-mono tabular-nums">
        {{ formatMonthLabel(m.month) }}
      </span>
    </div>
  </div>

  <!-- Legend -->
  <div class="flex items-center gap-4 mt-2 text-xs text-muted">
    <span class="flex items-center gap-1">
      <span class="inline-block w-3 h-3 rounded-sm bg-red-500" />Critical
    </span>
    <span class="flex items-center gap-1">
      <span class="inline-block w-3 h-3 rounded-sm bg-orange-400" />High
    </span>
    <span class="flex items-center gap-1">
      <span class="inline-block w-3 h-3 rounded-sm bg-amber-400" />Medium
    </span>
    <span class="flex items-center gap-1">
      <span class="inline-block w-3 h-3 rounded-sm bg-navy-400" />Low
    </span>
  </div>
</template>
